import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Truck } from "lucide-react";
import api, { formatPrice } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import { Countdown } from "@/components/Countdown";
import { toast } from "sonner";
import { useShipping } from "@/context/ShippingContext";
import { OlfactoryPyramid } from "@/components/OlfactoryPyramid";
import { fragranceAudience } from "@/lib/fragranceAudience";

const ProductDetail = () => {
  const { slug } = useParams();
  const { t, tl, lang } = useLang();
  const { free_shipping_threshold } = useShipping();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [variant, setVariant] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setVariant(r.data.variants[0]);
      api.get(`/products?category=${r.data.category}`).then((rr) =>
        setRelated(rr.data.filter((p) => p.slug !== slug).slice(0, 3))
      );
    });
  }, [slug]);

  if (!product || !variant) return <div className="min-h-[60vh]" data-testid="product-loading" />;

  const isFragrance = product.category === "profumi" || product.category === "fragranze-ambiente";

  const add = () => {
    if (!variant.in_stock) return;
    addItem({
      lookup_key: variant.lookup_key,
      name: product.name,
      format: variant.format,
      price: variant.discounted_price ?? variant.price,
      image: variant.image || product.image,
      quantity: 1,
    });
    toast.success(t("product.added"));
  };

  return (
    <div data-testid="product-detail-page" className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-16">
      <button
        onClick={() => navigate("/shop")}
        data-testid="product-back"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-stone-500 hover:text-charcoal transition-colors mb-10"
      >
        <ArrowLeft size={14} /> {t("product.back")}
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        {/* Image */}
        <div className="bg-white border border-stone-200 aspect-[4/5] overflow-hidden">
          <img src={variant.image || product.image} alt={`${tl(product.name)} — ${tl(variant.format)}`} className="w-full h-full object-cover" />
        </div>

        {/* Info */}
        <div className="flex flex-col">
          <p className="text-xs uppercase tracking-[0.2em] text-champagne">{tl(product.tagline)}</p>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-charcoal mt-4">{tl(product.name)}</h1>
          {fragranceAudience(product) && <span className="mt-3 w-fit border border-stone-200 px-3 py-1 text-xs uppercase tracking-wider text-stone-600">{tl(fragranceAudience(product))}</span>}

          {product.family && (
            <p className="text-sm text-stone-500 mt-3">
              <span className="uppercase tracking-[0.15em] text-[11px] text-stone-400 mr-2">{t("product.family")}</span>
              {tl(product.family)}
            </p>
          )}

          {product.discount_percent > 0 ? (
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <span className="line-through text-stone-400 text-xl">{formatPrice(variant.price)}</span>
              <span data-testid="product-price" className="text-2xl text-charcoal">{formatPrice(variant.discounted_price)}</span>
              <span className="bg-charcoal text-white text-[10px] uppercase tracking-[0.15em] px-2.5 py-1">-{product.discount_percent}%</span>
            </div>
          ) : (
            <p data-testid="product-price" className="text-2xl text-charcoal mt-6">{formatPrice(variant.price)}</p>
          )}

          {product.discount_percent > 0 && product.discount_ends_at && (
            <div data-testid="product-countdown" className="mt-3 inline-flex items-center gap-2 text-xs text-stone-600 bg-champagne-soft px-3 py-2 w-fit">
              <span className="uppercase tracking-[0.12em]">{t("offer.endsIn")}</span>
              <Countdown endsAt={product.discount_ends_at} className="text-charcoal" />
            </div>
          )}

          <p className={`mt-5 text-xs uppercase tracking-[0.14em] ${variant.in_stock ? "text-stone-600" : "text-red-700"}`}>
            {variant.in_stock ? t("product.available") : t("product.outOfStock")}
          </p>

          {/* Variants */}
          {product.variants.length > 1 && (
            <div className="mt-8">
              <p className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-3">{t("product.format")}</p>
              <div className="flex gap-3">
                {product.variants.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setVariant(v)}
                    data-testid={`variant-${v.id}`}
                    className={`px-5 py-3 text-sm border transition-colors ${
                      variant.id === v.id
                        ? "border-charcoal bg-charcoal text-white"
                        : "border-stone-300 text-stone-600 hover:border-charcoal"
                    }`}
                  >
                    {tl(v.format)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {isFragrance ? (
            <div className="mt-8 border-t border-stone-200 pt-7">
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500">
                {lang === "it" ? "Profilo olfattivo" : "Scent profile"}
              </p>
              <p className="mt-4 whitespace-pre-line text-sm leading-7 text-stone-600">
                {tl(product.description) || (lang === "it" ? "Descrizione olfattiva in aggiornamento." : "Scent description coming soon.")}
              </p>

              {product.olfactory_pyramid && (
                <div className="mt-8 border-t border-stone-200 pt-7">
                  <OlfactoryPyramid pyramid={product.olfactory_pyramid} noteImages={product.olfactory_note_images} lang={lang} />
                </div>
              )}
            </div>
          ) : (
            <div className="mt-8 border-t border-stone-200">
              <details className="group border-b border-stone-200">
                <summary className="flex cursor-pointer list-none items-center justify-between py-4 text-xs uppercase tracking-[0.16em] text-charcoal"><span>{lang === "it" ? "Descrizione" : "Description"}</span><span className="text-lg font-light text-champagne transition-transform group-open:rotate-45">+</span></summary>
                <p className="pb-5 whitespace-pre-line text-sm leading-7 text-stone-600">{tl(product.description) || (lang === "it" ? "Descrizione in aggiornamento." : "Description coming soon.")}</p>
              </details>
              <details className="group border-b border-stone-200">
                <summary className="flex cursor-pointer list-none items-center justify-between py-4 text-xs uppercase tracking-[0.16em] text-charcoal"><span>{lang === "it" ? "Modo d’uso" : "How to use"}</span><span className="text-lg font-light text-champagne transition-transform group-open:rotate-45">+</span></summary>
                <p className="pb-5 whitespace-pre-line text-sm leading-7 text-stone-600">{tl(product.usage) || (lang === "it" ? "Modo d’uso in aggiornamento." : "Directions coming soon.")}</p>
              </details>
              <details className="group border-b border-stone-200">
                <summary className="flex cursor-pointer list-none items-center justify-between py-4 text-xs uppercase tracking-[0.16em] text-charcoal"><span>{t("product.benefits")}</span><span className="text-lg font-light text-champagne transition-transform group-open:rotate-45">+</span></summary>
                <ul className="space-y-3 pb-5">{(tl(product.benefits) || []).map((benefit, index)=><li key={index} className="flex items-start gap-3 text-sm leading-6 text-stone-600"><Check size={15} className="mt-1 shrink-0 text-champagne"/>{benefit}</li>)}</ul>
              </details>
              <details className="group border-b border-stone-200">
                <summary className="flex cursor-pointer list-none items-center justify-between py-4 text-xs uppercase tracking-[0.16em] text-charcoal"><span>{lang === "it" ? "Ingredienti" : "Ingredients"}</span><span className="text-lg font-light text-champagne transition-transform group-open:rotate-45">+</span></summary>
                <p className="pb-5 whitespace-pre-line break-words text-xs leading-6 text-stone-500">{tl(product.ingredients) || (lang === "it" ? "INCI in aggiornamento." : "Ingredients list coming soon.")}</p>
              </details>
            </div>
          )}

          {product.catalog_url && (
            <a
              href={product.catalog_url}
              target="_blank"
              rel="noreferrer"
              className="mt-6 border border-stone-300 px-5 py-4 text-center text-[10px] uppercase tracking-[0.16em] text-charcoal transition-colors hover:border-charcoal hover:bg-stone-50"
            >
              {lang === "it"
                ? "Scopri la corrispondenza nel catalogo WhatsApp"
                : "View the matching fragrance in the WhatsApp catalogue"}
            </a>
          )}

          <Button
            onClick={add}
            disabled={!variant.in_stock}
            data-testid="add-to-cart-button"
            className="mt-10 rounded-none bg-charcoal text-white hover:bg-stone-800 h-14 text-xs uppercase tracking-[0.2em]"
          >
            {variant.in_stock ? t("product.addToCart") : t("product.outOfStock")}
          </Button>

          <div className="flex items-center gap-2 mt-5 text-xs text-stone-500">
            <Truck size={15} strokeWidth={1.5} className="text-champagne" />
            {lang === "it" ? `Spedizione gratuita da ${formatPrice(free_shipping_threshold)}` : `Free shipping over ${formatPrice(free_shipping_threshold)}`}
          </div>

        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <div className="mt-28">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight text-charcoal mb-12">{t("product.related")}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
            {related.map((p, i) => (
              <Reveal key={p.slug} delay={i * 100}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
